import { atom, waitForAny } from "recoil";
import { isSuccess } from "util/Common";
import {
  selectorFamilyWithoutCache as selectorFamily,
  selectorWithoutCache as selector,
} from "util/RecoilWithoutCache";
import {
  ContractPositionLevelList,
  createDefaultMixContractInfo,
} from "store/data/Contract";
import { createEmptyMixTickerInfo } from "./Ticker";
import { MixContractInfo, MixContractTickerInfo } from "../entity";
import { BusinessLineEnum } from "constants/base";
import {
  SearchStrategyTypeEnum,
  StrategyBusinessLine,
  createDefaultStrategy,
  StrategyEntryTabType,
  SecondStrategyType,
  StrategyCouponGuideStatus,
} from "constants/strategy";
import { RouteToBusinessLine } from "constants/base";
import { intercept } from "helper/formatter";
import {
  getBusinessLineConfigPromise,
  getAllCommonCategory,
  getAllSymbol,
  getAllCommonCategoryNew,
  queryHistoryTriggers,
} from "api/stragety";
import { defaultAI, getDefaultConfig } from "api/stragety/index";
import { getInvestConfig } from "api/stragety/timing";
import { getDcaConfig, getSelectStrategyData } from "api/stragety/dca";
import { getHoardAiDetail, getPortfolioConfig } from "api/stragety/hoard";
import { CurrentBusinessLineContractListState as CurrentContractListState } from "store/data/Contract";
import { MixSpotConfigListState } from "store/data/Spot";
import LocalCache, { CacheKey } from "util/LocalCache";
import { HistoryListProps } from "api/stragety/sign";
import { getMoonGridPlaceParam } from "api/stragety/moon";
import { LocaleSelector, LocaleState } from "./Base";
import { PositionLeverageLevel } from "store/entity";
import { queryClient } from "@app/client/queryClient";
import {
  getStrategyFuturesTicker,
  getStrategySpotTicker,
} from "api/ssr/ticker";
import { TicketByStrategyRes } from "api/stragety/coupon";
import { LocalCacheKey } from "constants/cacheKeys";
import { LanguageTypeMap } from "constants/vars";
import LocalCookie, { CookieKey } from "util/LocalCookie";
import { useParams } from "react-router-dom";

// 策略类型
export const StrategyTypeEnum = {
  dca: 2,
  grid: 1,
  timing: 5,
};
//盈亏参照
export const PROFIT_TYPEENUM = {
  total: 1,
  first: 2,
};
export const APPEND_TYPEENUM = {
  last: 2,
  first: 1,
};

/**
 * 当前交易对配置信息
 */
export const MixContractInfoState = atom({
  key: "mix-strategy-info-state",
  default: createDefaultMixContractInfo(),
});

export const CurrentBusinessLineContractListState = atom<MixContractInfo[]>({
  key: "current-business-line-strategy-list",
  default: [],
});

export const FullScreenVisible = atom({
  key: "FullScreenVisible",
  default: false,
});
export const MenuIsHide = atom({
  key: "MenuIsHide",
  default: false,
});

/**
 * 刷新订单参考
 */
export const refreshOrder = atom({
  key: "mix-refresh-order",
  default: false,
});

/**
 * 当前是网格还是dca
 */
export const MixStrategyType = selector({
  key: "mix-strategy-type",
  get: ({ get }) => {
    const { strategyType } = get(MixCurrentStrategyLineSymbolId);
    return strategyType;
  },
});

/**
 * 策略业务线（请使用 MixStrategyBusinessLineSelector）
 */
export const MixStrategyBusinessLine = atom({
  key: "mix-strategy-business-line",
  default: BusinessLineEnum.SPOT_BL,
});

/**
 *策略业务线selector
 */
export const MixStrategyBusinessLineSelector = selector({
  key: "mix-strategy-business-line-selector",
  get: ({ get }) => {
    const { line } = get(MixCurrentStrategyLineSymbolId);
    return RouteToBusinessLine[line];
  },
});

/**
 * AI策略参数，有后端直接提供
 */
export type AIStrategyParams = {
  daysRateProfit: string; // 七日年化
  oneGridMaxProfit: string; // 网格收益的上下限
  oneGridMinProfit: string;
  upLimitPrice: string; // 网格价格上下限
  downLimitPrice: string;
  strategyNum: string; // 网格数量
  bitMode: number;
};
type KeyValuePair = {
  key: string;
  value: string;
};
/**
 * 策略配置
 * 仅包含与网格策略相关的内容，其他配置请从业务配置中获取
 */
export type StrategyConfig = {
  feeRate: number | string;
  keepMarginRate: number | string;
  maxLeverage: number | string;
  openCostUpRatio?: number | string;
  minRealTokenBaseCount?: number | string;
  businessLine: number;
  symbolId: string;
  strategyMaxNum: number; // 支持的最大最小网格数，这个是硬性约束，通过最大最小价格计算出来的与之取小
  strategyMinNum: number;
  minRealTokenQuoteCount: string; // 最小下单额度，类似市价买入时的最小额度
  strategyModel: KeyValuePair[]; // 挂单模式：key-1等差，key-2等比
  strategyModelGrade: KeyValuePair[]; // 开单限价：key-1百分之1，key-2百分之5，key-3无限价
  strategyAttribute: KeyValuePair[]; // 1-AI创建 2-手动创建
  tradeStrategyRateDTO: AIStrategyParams; // AI参数
  tokenTrade: KeyValuePair[]; // 支持的投资币种，币币特殊处理，合约相当于可用保证金
  config: {
    feeRate: number;
    minRealTokenQuoteCount: number;
    minRealTokenBaseCount: number;
    appendMaxCount: number;
    keepMarginRate: number | string;
    symbolId: string;
    tokenId: string;
    tokenTrade: {};
    priceScale?: number;
    amountScale?: number;
    tokenScale?: number;
    investMaxAmount?: number | string;
    propertyHighPlace?: number | string;
    bondPrice?: number | string;
  }; // 新改版的接口配置文件
  fast: {}; //dca 快捷 配置文件
};

export type MixContractPlaceInfo = {
  baseCoinPlace: number; // 左币精度，用于深度数量等
  capitalRatePlace: number; // 资金费率精度
  delegatePricePlace: number; // 委托价格精度
  longLimitBurstPrice: number; // 多仓爆仓价下限，低于该值将显示永不爆仓
  marginRatePlace: number; // 保证金率精度
  priceEndStep: number; // 价格步长
  pricePlace: number; // 一般价格精度，多用于合理标记价格
  propertyHighPlace: number; // 高精度资产，多用于已实现盈亏等
  propertyPlace: number; // 一般资产精度，多用于可用等
  serverCalPlace: number; // 总计算精度，暂时不适用
  shortLimitBurstPrice: number; // 空仓爆仓上线，高于该值将显示永不爆仓
  transferInOutPlace: number; // 不明
  volumePlace: number; // 数量精度，委托数量使用精度
  minDelegateCount: string; //最小下单数量
  tokenTrade?: any;
  strategyMaxNum: number;
  strategyMinNum: number;
};

export type FutureBusinessConfig = {
  baseSymbol: string;
  businessLine: number;
  businessType: number; // 业务线，暂时没有枚举
  feeRate: number; // taker手续费率
  feeRateUpRatio: number; // 开仓成本-手续费上浮比例
  openCostUpRatio: number; // 开仓成本-基本上浮比例
  pricedSymbol: string;
  symbolCode: string; // 相当于原来的productCode
  symbolCodeDisplayName: string;
  symbolId: string;
  placeConfig: MixContractPlaceInfo;
  buyLimitPriceRatio: number; // 最高买价比例
  sellLimitPriceRatio: number; // 最低卖价比例
  supportMarginTokenIds: string[]; // 支持的币种tokenId
  indexDataSource: string;
  betaShow: boolean; // 是否仅在灰度展示
};

/** 当前的策略业务线 */
export const MixCurrentStrategyLine = selector({
  key: "curretn-strategy-line",
  get: ({ get }) => {
    const { line } = get(MixCurrentStrategyLineSymbolId);
    return line;
  },
});
/** new当前的策略业务线 */
export const NewMixCurrentStrategyLine = selector({
  key: "new-curretn-strategy-line",
  get: ({ get }) => {
    const { line } = get(NewMixCurrentStrategyLineSymbolId);
    return line;
  },
});

export const MixStrategySymbolListState = atom({
  key: "strategy-dashbord-symbol-list",
  default: [],
});

export const MixStrategyInfoState = atom({
  key: "strategy-symbol-info",
  default: createDefaultMixContractInfo(),
});

export const MixStrategyAllTickers = atom({
  key: "strategy-all-tickers",
  default: [],
});

/**
 * 排序后的ticker
 */
export const MixStrategyAllTickersSort = selector({
  key: "strategy-all-tickers-sort",
  get: ({ get }) => {
    const { whiteList } = get(MixStrategyConfigState);
    const tickersMap = get(MixStrategyAllTickersMap);
    const baseInfo = get(MixContractInfoStateSelector);
    const whiteSymbol = [];
    whiteList?.symbolWhiteList?.forEach((val) => {
      const key = getTickerKey({
        ...val,
        businessLine: BusinessLineEnum["SPOT_BL"],
      });
      if (tickersMap.get(key)) {
        whiteSymbol.push({
          ...tickersMap.get(key),
          symbolId: val?.symbolId,
          incomeMarkup: val?.incomeMarkup,
        });
      }
    });
    const handleAllTicker = whiteSymbol?.map((item) => {
      const baseVolumeNum = intercept(
        +item.baseVolume,
        baseInfo?.placeConfig.volumePlace
      );
      return {
        ...item,
        baseVolumeNum,
      };
    });
    return handleAllTicker || [];
  },
});

export interface lineDicType {
  SPOT: number | string;
  TRADE: number | string;
}

export const lineDic: lineDicType = {
  SPOT: 1,
  TRADE: 2,
};

export const lineParamsDic = {
  GRID: "grid",
  DCA: "dca",
  TIMING: "timing",
  CTA: "cta",
  WISE: "wise",
  HOARD: "hoard",
  TV: "tv",
  Moon: "moon",
};

// 弃用
export const MixCurrentStrategyMode = selector({
  key: "mix-current-strategy-mode",
  get: ({ get }) => {
    let line: number | string = 0;
    const currentLine = get(MixCurrentStrategyLine);
    if (currentLine === StrategyBusinessLine.SPOT_STRATEGY) {
      line = lineDic["SPOT"];
    } else if (currentLine === StrategyBusinessLine.FUTURE_STRATEGY) {
      line = lineDic["TRADE"];
    }
    return line;
  },
});

/**
 * 网格、dca 1: 自动 2:手动
 * cta 1.ALL 4: MACD 5: BOLL 6: MA 7: RSI
 */
export const MixActiveKey = atom({
  key: "strategy-active-key",
  default: Number(LocalCache.get(CacheKey.LOCAL_STRATEGY_ACTIVE_KEY)) || 1,
});
/**
 * 当前交易对的Ticker信息
 */
export const MixStrategyCurrentTicker = selector({
  key: "strategy-current-tickers",
  get: ({ get }) => {
    const list = get(MixStrategyAllTickers);
    const contractInfo = get(MixContractInfoStateSelector);
    const ticker = list.find((ticker) => {
      return ticker?.symbolCode === contractInfo?.symbolCode;
    });
    let tickerHttp = null;
    if (
      contractInfo?.businessLine &&
      contractInfo?.businessLine === BusinessLineEnum.SPOT_BL
    ) {
      const tickerHttpData = queryClient.getQueryData<MixContractTickerInfo>(
        getStrategySpotTicker.queryKey
      );
      tickerHttp = tickerHttpData?.[0];
    } else {
      tickerHttp = queryClient.getQueryData<MixContractTickerInfo>(
        getStrategyFuturesTicker.queryKey
      );
    }
    return {
      ...(ticker || tickerHttp || createEmptyMixTickerInfo()),
      symbolId: tickerHttp?.symbolId || contractInfo?.symbolId,
      symbolDisplayName:
        contractInfo?.symbolDisplayName ||
        tickerHttp?.symbolDisplayName ||
        tickerHttp?.symbolCode ||
        `${tickerHttp?.baseSymbol}/${tickerHttp?.pricedSymbol}`,
      baseSymbolId: contractInfo?.baseSymbolId || tickerHttp?.baseSymbolId || 0,
    };
  },
});

/**
 * 当前指定交易对的Ticker信息
 */
export const MixStrategyTokenInfo = selectorFamily({
  key: "strategy-token-info",
  get:
    (symbolCode: string) =>
    ({ get }) => {
      const contractList = get(CurrentBLineContractListLodable);
      return contractList.find((i) => i.symbolId === symbolCode);
    },
});

export function getTickerKey(
  info: MixContractInfo | MixContractTickerInfo | any
): string {
  // return info.baseSymbol + '_' + info.symbolCode
  return info.symbolCode + info.businessLine;
}

/**
 * 当前业务线所有的ticker
 */
export const CurrentSpotBusinessTickers = selectorFamily({
  key: "current-spot-business-tickers",
  get:
    (catrgory = "-1") =>
    ({ get }) => {
      // const contractList = get(MixStrategySymbolListState)
      const currentTagCoins = get(MixStrategySupportListSelector(catrgory));
      const tickersMap = get(MixStrategyAllTickersMap);
      if (!tickersMap?.size) return [];
      let newList = [];
      function formatTickerInfo({ ticker, coinInfo = null }) {
        let symbolId =
          ticker.symbolId || coinInfo
            ? coinInfo?.symbolCode + "_SPBL"
            : ticker?.symbolCode + "_SPBL";
        return {
          ...ticker,
          chgUTC: ticker.chgUTC ? ticker.chgUTC : "0",
          symbolId: symbolId,
          symbolName: ticker.baseSymbol
            ? ticker.baseSymbol.toUpperCase() +
              "/" +
              ticker.pricedSymbol.toUpperCase()
            : ticker.symbolDisplayName,
          symbolDisplayName: ticker.symbolDisplayName
            ? ticker?.symbolDisplayName.toUpperCase()
            : ticker?.baseSymbol.toUpperCase() +
              (ticker?.businessLine === BusinessLineEnum.SPOT_BL ? "/" : "") +
              ticker.pricedSymbol.toUpperCase(),
        };
      }
      // 当前分区下没有币对
      if (!currentTagCoins?.length) {
        return [];
        // tickersMap.forEach((ticker) => {
        //   newList.push(formatTickerInfo({ ticker }));
        // });
      } else {
        currentTagCoins?.forEach((coinInfo) => {
          const ticker =
            tickersMap.get(getTickerKey(coinInfo)) ||
            createEmptyMixTickerInfo();
          if (ticker?.baseSymbol) {
            newList.push(formatTickerInfo({ ticker, coinInfo }));
          }
        });
      }
      return newList;
    },
});

/**
 * 搜索ticker
 */
export const MixSearchTickers = selectorFamily<
  any,
  { businessline: string | number; strategyType: string | number }
>({
  key: "current-search-tickers",
  get:
    ({ businessline, strategyType }) =>
    ({ get }) => {
      const list = get(
        MixStrategySupportListFamily({
          businessLine: businessline,
        })
      );
      if (strategyType === "wise") return []; // wise不需要筛选币对 直接显示全部
      const dcaOrgrid = {
        grid: 1,
        dca: 2,
        timing: 5,
        cta: 1, // 产品同学说cta支持的币对跟网格一样 使用网格币对列表
        hoard: 12,
      };

      const newList: dataProps[] = list.filter(
        (val) => val?.businessLine === businessline
      );
      let searchData: strategyDataItemProps[] | undefined = [];
      if (!newList?.length) return [];

      if (businessline === BusinessLineEnum.USDT_MIX_CONTRACT_BL) {
        const searchList = newList?.[0]?.values;
        if (strategyType === "all") {
          return searchList;
        }
        //筛选出合约部分 categoryId 为空,还要再筛选出当前支持的是dca 还是 grid
        searchData = searchList?.filter((val) => {
          return (
            val?.supportStrategyTypes.includes(dcaOrgrid[strategyType]) ||
            !val?.supportStrategyTypes.length
          );
        });
      } else {
        const searchList = newList?.find((val) => {
          return val?.categoryId === "-1";
        })?.values;
        if (strategyType === "all") {
          return searchList;
        }

        //筛选对应的分类币对。还要再筛选出当前支持的是dca 还是 grid
        searchData = searchList.filter((val) => {
          return (
            val?.supportStrategyTypes.includes(dcaOrgrid[strategyType]) ||
            !val?.supportStrategyTypes.length
          );
        });
      }
      return searchData?.map((val) => {
        return {
          ...val,
          businessline,
        };
      });
    },
});

/**
 *
 * 基于AllTickersState生成的map
 * 以businessType+symbolCode生成Key
 */
export const MixStrategyAllTickersMap = selector<
  Map<string, MixContractTickerInfo>
>({
  key: "strategy-all-tickers-map",
  get: ({ get }) => {
    const tickers = get(MixStrategyAllTickers);
    return tickers.reduce((map, ticker) => {
      const key = getTickerKey(ticker);
      map.set(key, ticker);
      return map;
    }, new Map());
  },
});

/**
 * 默认AI参数，使用时记得处理空的情况
 */
export function createDefaultAIParams(): AIStrategyParams {
  return {
    downLimitPrice: "",
    oneGridMaxProfit: "",
    oneGridMinProfit: "",
    strategyNum: "",
    upLimitPrice: "",
    daysRateProfit: "",
    bitMode: 1,
  };
}
export const MixStrategySymbolId = selector({
  key: "strategy-strategy-symboid",
  get: ({ get }) => {
    const { symbolId } = get(MixCurrentStrategyLineSymbolId);
    return symbolId;
  },
});

/**
 * 获取AI创建网格的数据
 */
export const MixStrategyAIDataState = selector({
  key: "strategy-ai-data",
  // default: createDefaultAIParams()
  get: async ({ get }) => {
    const symbolId = get(MixStrategySymbolId);
    const strategyType = get(MixStrategyType);
    const businessLine = get(MixStrategyBusinessLineSelector);
    const clickType = get(MixStrategyDirect);
    if (businessLine && clickType && strategyType === "grid") {
      const res: any = await defaultAI({
        symbolId: symbolId,
        businessLine: businessLine,
        gridType: Number(clickType),
      });
      return res?.data;
    }
    return createDefaultAIParams();
  },
});

/**
 * 获取当前策略
 */
export const MixStrategyCurrentList = atom({
  key: "strategy-current-list",
  default: [],
});

/**
 * 业务线的业务类型
 * 和撮合类型不同，每个业务线有自己的instType，用于当前委托等业务线相关的数据
 * @param businessLine
 * @returns {string}
 */
export function getBusinessLineCode(businessLine) {
  switch (businessLine) {
    case BusinessLineEnum.SPOT_BL:
      return "spbl";
    case BusinessLineEnum.USDT_MIX_CONTRACT_BL:
      return "umcbl";
    case BusinessLineEnum.USD_MIX_CONTRACT_BL:
      return "dmcbl";
    default:
      return "";
  }
}

export function createDefaultMixContractPlaceInfo(): MixContractPlaceInfo {
  return {
    baseCoinPlace: 2,
    capitalRatePlace: 4,
    delegatePricePlace: 2,
    longLimitBurstPrice: 0.0001,
    marginRatePlace: 2,
    priceEndStep: 1,
    pricePlace: 2,
    propertyHighPlace: 8,
    propertyPlace: 4,
    serverCalPlace: 12,
    shortLimitBurstPrice: 99999999,
    transferInOutPlace: 4,
    volumePlace: 4,
    minDelegateCount: "0.0001",
    tokenTrade: "",
    strategyMaxNum: 0,
    strategyMinNum: 0,
  };
}

export function defaultConfigState(): any {
  return Object.assign(
    createDefaultMixContractInfo(),
    createDefaultStrategyConfig()
  );
}

export function createDefaultStrategyConfig(): StrategyConfig {
  return {
    feeRate: 0.002,
    keepMarginRate: 0,
    maxLeverage: 128,
    minRealTokenQuoteCount: "5",
    minRealTokenBaseCount: "0.0001",
    openCostUpRatio: "0.1",
    strategyAttribute: [],
    strategyMaxNum: 0,
    strategyMinNum: 0,
    strategyModel: [],
    strategyModelGrade: [],
    symbolId: "",
    tradeStrategyRateDTO: createDefaultAIParams(),
    businessLine: 1,
    tokenTrade: [{ key: "0", value: "USDT" }],
    config: {
      minRealTokenQuoteCount: 0,
      minRealTokenBaseCount: 0,
      appendMaxCount: 0,
      keepMarginRate: "0.02",
      symbolId: "BTCUSDT_UMCBL",
      tokenId: "USDT",
      tokenTrade: {},
      priceScale: 2,
      amountScale: 4,
      tokenScale: 4,
      feeRate: 0,
    },
    fast: {},
  };
}

const getConfig = async ({
  businessLine,
  strategyType,
  clickType,
  symbolId,
}) => {
  if (businessLine && strategyType) {
    if (!["timing", "hoard", "tv"].includes(strategyType)) {
      if (!clickType) return {};
    }
    let params = {
      symbolId: symbolId,
      businessLine: businessLine,
    };
    let promise;
    if (strategyType === "dca") {
      params = Object.assign(params, {
        dcaType: Number(clickType),
      });
      promise = getDcaConfig(params);
    } else if (strategyType === "grid" || strategyType === "moon") {
      params = Object.assign(params, {
        gridType: Number(clickType),
        businessLine: businessLine,
      });
      promise = getDefaultConfig(params);
    } else if (["timing", "hoard", "tv"].includes(strategyType)) {
      promise = getInvestConfig();
    }
    const res = await promise;
    if (isSuccess(res?.code)) {
      if (["timing", "hoard", "tv"].includes(strategyType)) {
        return {
          whiteList: res?.data?.investToken[0],
          investMaxTokenCount: res?.data?.investMaxTokenCount || 10,
        };
      }
      //dca 和cta 数据结构不同
      const queryLimitRate =
        res?.data?.config?.queryLimitRate || res?.data?.queryLimitRate;
      if (queryLimitRate) {
        LocalCache.set(CacheKey.STRATEGY_API_LIMIT, queryLimitRate * 1000);
      }
      return res?.data;
    }
  }

  return strategyType === "timing"
    ? { whiteList: [], investMaxTokenCount: 10 }
    : Object.assign(
        createDefaultMixContractInfo(),
        createDefaultStrategyConfig()
      );
};

/**
 * 获取策略基础配置
 */
export const MixStrategyConfigState = selector({
  key: "mix-strategy-config",
  // default: Object.assign(createDefaultMixContractInfo(), createDefaultStrategyConfig())
  get: async ({ get }) => {
    const symbolId = get(MixStrategySymbolId);
    const strategyType = get(MixStrategyType);
    const businessLine = get(MixStrategyBusinessLineSelector);
    const clickType = get(MixStrategyDirect);
    return await getConfig({ businessLine, strategyType, clickType, symbolId });
  },
});

/**
 * 获取策略-囤币宝基础配置
 */
export const MixStrategyHoardConfigState = selector({
  key: "mix-strategy-hoard-config",
  // default: Object.assign(createDefaultMixContractInfo(), createDefaultStrategyConfig())
  get: async ({ get }) => {
    const symbolId = get(MixStrategySymbolId);
    return await getPortfolioConfig({ symbolId });
  },
});

/**
 * 获取策略基础配置
 */
type configDataProps = {
  symbolId: string;
  strategyType: string;
  businessLine: string;
  clickType: string;
};
export const MixStrategyConfigStateFamily = selectorFamily<
  any,
  configDataProps
>({
  key: "mix-strategy-config",
  // default: Object.assign(createDefaultMixContractInfo(), createDefaultStrategyConfig())
  get:
    ({ symbolId, strategyType, businessLine, clickType }) =>
    async () => {
      return await getConfig({
        businessLine,
        strategyType,
        clickType,
        symbolId,
      });
    },
});

/**
 * 币币改版，余币情况
 */
export type NewSpotWalletInfo = {
  coinId: string;
  coinName: string;
  available: string;
};

/**
 * 合约资产信息
 */
export const MixStrategyWalletState = atom({
  key: "mix-strategy-wallet",
  default: new Map(),
});

/**
 * 可用资产
 */
export const getStrateAvalidUsdt = selectorFamily({
  key: "mix-strate-avalid-usdt",
  get:
    (tokenId: string) =>
    ({ get }) => {
      const wallet = get(MixStrategyWalletState);
      return wallet.get(tokenId);
    },
});

export const createAIData2Input = () => {
  return {
    srcId: "",
    isCopy: false,
    strategyNumber: 0,
    gridProfitRangeDisplay: "",
    avalidPrice: 0,
    days7RateProfit: "",
    endOperate: 2,
    strategyAIData: {
      downLimitPrice: "0",
      upLimitPrice: "0",
      strategyNum: "",
      bitMode: 1,
      pricedCoinCountPercent: "",
      stopLossPrice: "",
      stopProfitPrice: "",
      triggerPrice: "",
      strategyModel: "",
      strategyModelGrade: "",
      endOperate: 2,
    },
    firstPrice: "",
    firstAmount: "",
    maxAppendCount: "",
    targetLoss: "",
    targetProfit: "",
    amountTimes: "",
    firstChangeRange: "",
    priceTimes: "",
    profitType: "",
    appendType: 1,
    investType: 1,
    loopTimes: 0,
    leverage: 0,
  };
};
/**
 * 设置AI参数到手动填写的位置
 */
export const getStrategyAIData2Input = atom({
  key: "mix-aidata-2-input",
  default: createAIData2Input(),
});

/**
 * 合约策略的配置
 */
export const MixFutureBusinessConfig = atom({
  key: "mix-future-business-config",
  default: createDefaultMixContractInfo(),
});

/**
 * 当前支持的列表
 */
export const MixStrategySupportList = atom({
  key: "mix-strategy-support-list",
  default: [],
});
/**
 * 支持策略交易对信息
 */
export type BaseSymbolInfo = {
  businessLine: number;
  symbolId: string;
  symbolCode: string;
  tokenId: string;
  symbolDisplayName: string;
  baseSymbol: string;
  pricedSymbol: string;
  tokenBaseId: number;
  tokenQuoteId: number;
  supportGridTypes: number[];
};

export function createDefaultBaseSymbolInfo(): BaseSymbolInfo {
  return {
    businessLine: 1,
    pricedSymbol: "",
    symbolCode: "",
    symbolDisplayName: "",
    symbolId: "",
    tokenId: "",
    baseSymbol: "",
    tokenQuoteId: 0,
    tokenBaseId: 0,
    supportGridTypes: [1, 2],
  };
}

/**
 * 基础配置
 * STRATEGY_BASE_SPOT_CONFIG
 */
export const MixStrategyBaseSpotConfig = atom({
  key: "mix-strategy-base-spot-config",
  default: createDefaultBaseSymbolInfo(),
});

/**
 * 获取设置输入的值
 */
export const MixStrategyInputData = atom({
  key: "mix-strategy-input-data",
  default: createDefaultStrategy(),
});

/**
 * 当前选择的gridType  或者 dcaType 正方向 和 多空头
 * 1-正向/多头 2-反向/空头
 */
export const MixStrategyDirect = atom<number>({
  key: "mix-strategy-direct",
  default: 0,
});

/**
 * 是否刷新合约网格的资产信息
 */
export const MixRefreshWallet = atom({
  key: "mix-refresh-wallet",
  default: false,
});

/**
 * 按照合约symbolId查询对应的配置信息
 */
export const getMixStrategyInfoBySymbolId = selectorFamily<
  MixContractInfo & { contractLevelList: PositionLeverageLevel[] },
  string
>({
  key: "get-mix-strategy-info-selector",
  get:
    (symbolId: string) =>
    ({ get }) => {
      const base = get(CurrentBLineContractListLodable);
      const info =
        base.find((config) => config.symbolId === symbolId) ||
        createDefaultMixContractInfo();
      const pageFrom = get(MixIsContractPage);
      if (pageFrom !== "ct") return info;
      const contractLevelList = get(ContractPositionLevelList(symbolId));
      return { ...info, contractLevelList };
    },
});

export const getAssetsMixStrategyInfoBySymbolId = selectorFamily<
  any,
  { symbolId: string; businessLine: string | number }
>({
  key: "get-mix-strategy-info-selector",
  get:
    (params) =>
    ({ get }) => {
      const { symbolId, businessLine } = params;
      const base = get(CurrentAssetsBLineContractListLodable(businessLine));
      const info =
        base.find((config) => config.symbolId === symbolId) ||
        createDefaultMixContractInfo();
      const pageFrom = get(MixIsContractPage);
      if (pageFrom !== "ct") return info;
      const contractLevelList = get(ContractPositionLevelList(symbolId));
      return { ...info, contractLevelList };
    },
});

/**
 * ============================================================================
 * 以下为优化内容
 * lodable
 * ============================================================================
 */

/** 当前的策略业务线 */
export const MixCurrentStrategyLineSymbolId = atom({
  key: "curretn-strategy-line-symbolId",
  default: {
    line: "spot-strategy",
    symbolId: "",
    strategyType: "",
  },
});

/** new 当前的策略业务线 */
export const NewMixCurrentStrategyLineSymbolId = atom({
  key: "new-curretn-strategy-line-symbolId",
  default: {
    line: "spot-strategy",
    symbolId: "",
    strategyType: "",
  },
});

export type strategyDataItemProps = {
  symbolCode: string;
  symbolId?: string;
  symbolDisplayName?: string;
  supportStrategyTypes?: number[];
} & MixContractTickerInfo;
type dataProps = {
  categoryId: string;
  values: strategyDataItemProps[];
};

/**
 * 获取不同分类支持的列表
 */
export const MixStrategySupportListSelector = selectorFamily({
  key: "mix-strategy-support-selector-list",
  get:
    (categoryId = "-1") =>
    ({ get }) => {
      // 获取分类列表
      const list = get(MixStrategySupportListSimple);

      let ret = [];

      let currentList = list.find((c) => c.categoryId == categoryId);
      currentList?.values?.forEach((item) => {
        ret.push({
          symbolCode: item.symbolId.replace("_SPBL", ""),
          businessLine: BusinessLineEnum.SPOT_BL,
        });
      });

      return ret;
      // const businessLine = get(MixStrategyBusinessLineSelector);

      // const newList: dataProps[] = list.filter(
      //   (val) => val?.businessLine === businessLine
      // );

      // let searchData: strategyDataItemProps[] | undefined = [];
      // if (!newList?.length) return [];
      // if (businessLine === BusinessLineEnum.USDT_MIX_CONTRACT_BL) {
      //   //筛选出合约部分 categoryId 为空,还要再筛选出当前支持的是dca 还是 grid
      //   searchData = newList?.[0]?.values;
      // } else {
      //   //筛选对应的分类币对。还要再筛选出当前支持的是dca 还是 grid
      //   searchData = newList?.find((val) => {
      //     return val?.categoryId === categoryId;
      //   })?.values;
      // }
      // return searchData?.map((val) => {
      //   return {
      //     symbolCode: val,
      //     businessLine,
      //   };
      // });
    },
});
/**
 * 获取所有现货分类
 */
export const MixStrategySupportCategorySelector = selector({
  key: "mix-strategy-support-category-selector-list",
  get: ({ get }) => {
    // const list = get(MixStrategySupportListLodable)
    const catelist = get(MixStrategySupportListSimple);
    let cates: any = [];
    catelist.map((item) => {
      cates.push({
        name: item.categoryName,
        key: item.categoryId,
      });
    });
    return cates;
  },
});

export const MixStrategySupportListLodable = selector({
  key: "mix-strategy-support-list-lodable",
  get: async ({ get }) => {
    const stragetyType = get(MixStrategyType);
    const strategyLine = get(MixCurrentStrategyLine);
    const cateList: any = await getAllCommonCategory({
      strategyType: SearchStrategyTypeEnum?.[stragetyType]?.[strategyLine],
    });
    if (isSuccess(cateList?.code)) {
      return cateList?.data;
    }
    return [];
  },
});

export const MixStrategySupportListSimple = selector({
  key: "mix-strategy-support-list-lodable-simple",
  get: async () => {
    // const localMd5Key = LocalCacheKey.SPOT_PARTITION_MD5;
    // const localDataKey = LocalCacheKey.SPOT_PARTITION_MD5_DATA;
    // const oldMd5Value = localStorage.getItem(localMd5Key);
    // const params = {
    //   md5Value: oldMd5Value,
    //   showWatchGroup: true,
    //   // region: true,
    //   languageType: LanguageTypeMap[LocalCookie.get(CookieKey.LANG)],
    // };

    const res: any = await getAllCommonCategoryNew();
    console.log("res: ", res);

    let items: any = res?.data || [];

    // const md5Value = res.data?.md5Value;

    // if (md5Value) {
    //   if (oldMd5Value !== md5Value) {
    //     localStorage.setItem(localMd5Key, md5Value);
    //     localStorage.setItem(localDataKey, JSON.stringify(items));
    //   } else {
    //     try {
    //       items = JSON.parse(localStorage.getItem(localDataKey));
    //     } catch (error) {
    //       console.log("error: ", error);
    //     }
    //   }
    // }

    return items;
  },
});

export const MixStrategySupportListFamily = selectorFamily<
  any,
  { businessLine: string | number }
>({
  key: "mix-strategy-support-list-lodable",
  get:
    ({ businessLine }) =>
    async () => {
      const cateList: any = await getAllSymbol({
        businessLines: [+businessLine],
      });
      if (isSuccess(cateList?.code)) {
        return cateList?.data;
      }
      return [];
    },
});

export const MixContractInfoStateSelector = selector({
  key: "mix-strategy-info-state-lodable",
  get: ({ get }) => {
    const symbolId = get(MixStrategySymbolId);
    const data = get(CurrentBLineContractListLodable);

    let baseInfo = data?.find((config) => config.symbolId === symbolId);
    // console.log("baseInfo: ", symbolId, baseInfo, data);

    if (!baseInfo) {
      baseInfo = data?.[0];
    }
    return baseInfo || createDefaultMixContractInfo();
  },
});

export const CurrentSpotListLodable = selector({
  key: "current-spot-strategy-list-lodable",
  get: async ({ get }) => {
    // 英国合规需要locale参数
    const locale = get(LocaleState);
    let tmp2 = [];
    const result: any = await getBusinessLineConfigPromise([1], locale);
    if (result.code === "00000" && result.data?.items?.length) {
      tmp2 = result.data.items[0].symbolDTOList || [];
    }
    return tmp2;
  },
});

export const CurrentFutureListLodable = selector({
  key: "current-future-strategy-list-lodable",
  get: async () => {
    let tmp2 = [];
    const result: any = await getBusinessLineConfigPromise([10, 11]);
    if (result?.code === "200") {
      let tmp = result.data?.listDTOS || [];
      tmp.forEach((item) => {
        if (item.businessLine === BusinessLineEnum.USDT_MIX_CONTRACT_BL) {
          tmp2 = tmp2.concat(item.symbolDTOList);
        }
      });
    }
    return tmp2;
  },
});

export const CurrentBLineContractListLodable = selector<
  (MixContractInfo & { contractLevelList?: PositionLeverageLevel[] })[]
>({
  key: "current-business-line-strategy-list-lodable",
  get: ({ get }) => {
    const businessLine = get(MixStrategyBusinessLineSelector);
    const pageFrom = get(MixIsContractPage);
    if (pageFrom === "ct") {
      const contractList = get(CurrentContractListState);
      if (contractList?.length) {
        return contractList;
      }
    }
    if (pageFrom === "spot") {
      const spotBList = get(MixSpotConfigListState);
      if (spotBList?.length) {
        return spotBList;
      }
    }
    const data = get(
      waitForAny({
        futureList: CurrentFutureListLodable,
        spotList: CurrentSpotListLodable,
      })
    );
    const { spotList, futureList } = data;

    if (businessLine === BusinessLineEnum["USDT_MIX_CONTRACT_BL"]) {
      return futureList?.state === "hasError" ? [] : data?.futureList?.contents;
    } else if (businessLine === BusinessLineEnum["SPOT_BL"]) {
      return spotList?.state === "hasError" ? [] : data?.spotList?.contents;
    } else {
      return [];
    }
  },
});

export const CurrentAssetsBLineContractListLodable = selectorFamily<any>({
  // (MixContractInfo & { contractLevelList?: PositionLeverageLevel[] })[]
  key: "current-assets-business-line-strategy-list-lodable",
  get:
    (isAssetsPageLine: number) =>
    ({ get }) => {
      let businessLine = get(MixStrategyBusinessLineSelector);
      if (isAssetsPageLine) {
        businessLine = +isAssetsPageLine; //总览页传业务线
      }

      const pageFrom = get(MixIsContractPage);
      if (pageFrom === "ct") {
        const contractList = get(CurrentContractListState);
        if (contractList?.length) {
          return contractList;
        }
      }
      if (pageFrom === "spot") {
        const spotBList = get(MixSpotConfigListState);
        if (spotBList?.length) {
          return spotBList;
        }
      }
      const data = get(
        waitForAny({
          futureList: CurrentFutureListLodable,
          spotList: CurrentSpotListLodable,
        })
      );
      const { spotList, futureList } = data;

      if (businessLine === BusinessLineEnum["USDT_MIX_CONTRACT_BL"]) {
        return futureList?.state === "hasError"
          ? []
          : data?.futureList?.contents;
      } else if (businessLine === BusinessLineEnum["SPOT_BL"]) {
        return spotList?.state === "hasError" ? [] : data?.spotList?.contents;
      } else {
        return [];
      }
    },
});
/**
 * 系统时间
 */
export const MixStrategySysTime = atom({
  key: "mix-strategy-syys-time",
  // default: localStorage.getItem('mix_requesttime') || Date.now()
  default: Date.now(),
});

/**
 * dca 用选择策略
 */
export const MixSelectDCAStrategy = atom({
  key: "mix-select-dca-strategy",
  default: true,
});
/*
 * 是否有可发布策略
 */

export const MixUserPublishStrategyState = atom({
  key: "mix-user-publish-strategy",
  default: false,
});

/**
 * 当前用户的策略交易身份
 */
export const MixStrategyUserType = atom({
  key: "mix-strategy-user-type",
  default: {
    userType: 0,
  },
});

/**
 * 策略卡片信息
 */
export const MixStrategyCardInfo = atom({
  key: "mix-strategy-card-info",
  default: {
    isOpenDrawerLoading: false, // 打开卡片的详情
  },
});

/**
 * 用于刷新策略参数数据
 */
export const MixRefreshStrategyData = atom({
  key: "Mix-refresh-strategy-data",
  default: {
    time: "",
  },
});
/**
 * 设置选择的策略参数
 */
export const MixGetSelectStrategyData = selector({
  key: "MixGetSelectStrategyData",
  get: async ({ get }) => {
    const strategyType = get(MixStrategyType);
    if (strategyType === "moon") {
      const mixRefreshStrategyData = get(MixRefreshStrategyData); // 用于刷新策略参数数据
      const businessLine = get(MixStrategyBusinessLineSelector);
      const mixStrategyDirect = get(MixStrategyDirect);
      const isFuture = +businessLine === BusinessLineEnum.USDT_MIX_CONTRACT_BL;
      const secondStrategyType = isFuture
        ? SecondStrategyType?.[mixStrategyDirect]?.["futures"]
        : SecondStrategyType?.[mixStrategyDirect]["spot"];
      const symbolId = get(MixStrategySymbolId);

      const res: any = await getMoonGridPlaceParam({
        symbolId,
        secondStrategyType,
        strategyType: isFuture
          ? SearchStrategyTypeEnum["moon"]["future-strategy"]
          : SearchStrategyTypeEnum["moon"]["spot-strategy"],
      });
      if (isSuccess(res?.code)) {
        return res?.data;
      }
      return {};
    }
    const id = get(MixCurrentStrategyId);
    if (!id) {
      return {};
    }
    const res: any = await getSelectStrategyData({ id });
    if (isSuccess(res?.code)) {
      return res?.data;
    }
    return {};
  },
});

/**
 * 下单结束后更新余额
 */
export const MixIsUpdateAssets = atom({
  key: "isUpdateAssets",
  default: false,
});

/**
 * 存储划线
 */
export const MixStrateLineList = atom({
  key: "MixStrateLineList",
  default: [],
});

/**
 * 是否是合约页面
 */
export const MixIsContractPage = atom<"spot" | "ct" | "strategy">({
  key: "MixIsContractPage",
  default: "strategy",
});

/**
 * 选择策略类型后展示表单
 */
export const MixIsShowStrategyForm = atom({
  key: "MixIsShowStrategyForm",
  default: false,
});

/**
 * 策略下单后，与策略列表联动，保存的参数
 * 确认下单后 联动策略列表展示对应的下单区选项
 */
export const MixOrderCreateInfo = atom({
  key: "MixOrderCreateInfo",
  default: {
    createNum: 0, // 是否下单，下单成功后改为true，下单区设置为false
    orderInfo: genMixOrderCreateInfoPropsConfig(),
  },
});

type MixOrderCreateInfoPropsConfig = {
  businessLine: number; // 业务线
  symbolId?: string;
  gridType?: number; // 用于判断是grid类型
  dcaType?: number; // 用于判断是dca类型
  ctaType?: number; // 用于判断cta类型
  wiseType?: number; // 用于判断cta类型
  investType?: number; // 下单参数 用于判断定投
  isTv?: boolean; //合约信号策略
  isHoard?: boolean; //囤币宝
};

// 创建策略的信息，根据这个信息来切换显示的类型
function genMixOrderCreateInfoPropsConfig(): MixOrderCreateInfoPropsConfig {
  return {
    businessLine: undefined,
    symbolId: undefined,
    gridType: undefined,
    dcaType: undefined,
    ctaType: undefined,
    wiseType: undefined,
    investType: undefined,
    isTv: false,
    isHoard: false,
  };
}
/* 
 当前运行的策略数据
 */
export const MixStrategyRuningInfo = atom({
  key: "MixStrategyRuningInfo",
  default: {
    totalCount: 0,
    totalInvestAmount: 0,
    totalProfit: 0,
  },
});

/* 
 总览页业务线
 */
export const AssetsBusinessLine = atom({
  key: "AssetsBusinessLine",
  default: 1,
});
/* 
 总览页策略类型
 */
export const AssetsStrategyType = atom({
  key: "AssetsStrategyType",
  default: 1,
});
/* 
 当前运行的策略数据
 */
export const MixContractStrategyListSelectType = atom({
  key: "MixContractStrategyListSelectType",
  default: {
    strategyType: "grid",
    listType: "currentList",
  },
});

/**
 * 策略入口Tab类型切换
 */
export const MixStrategyEntryTabType = atom<StrategyEntryTabType>({
  key: "MixStrategyEntryTabType",
  default: StrategyEntryTabType.Manual,
});

export const MixIsInitWs = atom({
  key: "MixIsInitWs",
  default: false,
});

/**
 * 当前选择的ai 策略ID
 */
export const MixCurrentStrategyId = atom<number | string>({
  key: "MixCurrentStrategyId",
  default: 0,
});

/**
 * 获取运行数量
 */
const defaultRunningData = () => {
  const data = SearchStrategyTypeEnum;
  const values = Object.values(data);
  let defaultData = [];
  values?.forEach((val) => {
    if (val?.["spot-strategy"]) {
      defaultData.push({
        [`c_${val?.["spot-strategy"]}`]: {
          strategyType: val?.["spot-strategy"],
          totalCount: 0,
        },
      });
    }
    if (val?.["future-strategy"]) {
      defaultData.push({
        [`c_${val?.["future-strategy"]}`]: {
          strategyType: val?.["future-strategy"],
          totalCount: 0,
        },
      });
    }
  });
  return defaultData;
};
/* 
 当前运行的策略数据
 */
export const MixStrategyRuningCount = atom({
  key: "MixStrategyRuningCount",
  default: defaultRunningData(),
});
/*
 * 当前选择的策略tab
 */
export const MixCurrentStrategyTab = atom({
  key: "MixCurrentStrategyTab",
  default: "spot-grid",
});
/**
 * 存储跟单信号的socket数据
 */
export const MixStrategySignMarker = atom({
  key: "MixStrategySignMarker",
  default: [],
});

/**
 * 存储K线当前的视图时间范围
 */
export const MixStrategyChartTimeRange = atom({
  key: "MixStrategyChartTimeRange",
  default: {
    from: 0,
    to: 0,
  },
});

/**
 * 存储跟单信号的历史数据
 */
const signDefaultData: HistoryListProps = {
  historySignalList: [],
  symbolCode: "",
  symbolUnit: "",
};
export const MixStrategyAllSignMarker = atom({
  key: "MixStrategyHistorySignMarker",
  default: signDefaultData,
});

/**
 * 是否是全屏tv
 */
export const MixStrategyIsFullScreen = atom({
  key: "MixStrategyIsFullScreen",
  default: false,
});
/*
 * 触发更新wise智投
 */
export const MixWiseTriggerUpdate = atom({
  key: "MixWiseTriggerUpdate",
  default: false,
});

/**
 * 当前选择的ai 策略ID,仅用做tv
 */
export const MixCurrentTVStrategyId = atom({
  key: "MixCurrentTVStrategyId",
  default: 0,
});

/**
 * Tv使用的当前信号
 */
export const MixCurrentTVStrategyInfo = atom({
  key: "MixCurrentTVStrategyInfo",
  default: {
    webhookUrl: "",
    webhookName: "",
    webhookProfile: "",
    id: "",
  },
});
/* 获取策略后端精度配置、存储精度
 * 设置选择的策略参数
 */
export const MixGetCtaPointList = selector({
  key: "MixGetCtaPointList",
  get: async ({ get }) => {
    const id = get(MixCurrentStrategyId);
    const mixSelectDCAStrategy = get(MixSelectDCAStrategy);
    const strategyType = get(MixStrategyType);
    if (!id || strategyType !== "cta" || mixSelectDCAStrategy) {
      return [];
    }
    const res: any = await queryHistoryTriggers({ id });
    if (isSuccess(res?.code)) {
      return res?.data?.list;
    }
    return [];
  },
});

/**
 * 获取策略后端精度配置、存储精度
 */
export const MixStrategyAccuracy = atom({
  key: "MixStrategyAccuracy",
  default: {
    percentagePlace: 0, // 百分比精度 末尾保存0
    profitPlace: 0, // 利润精度 末尾舍0
    init: false, // 是否初始化
  },
});

/**
 * 记录策略体验券弹窗状态
 */
export const MixStrategyCouponGuideDialogStatus = atom({
  key: "MixStrategyCouponGuideDialogStatus",
  default: StrategyCouponGuideStatus.UnFetch,
});

/**
 * 策略创建提示弹窗
 */
export const MixIsShowUpdateDialog = atom({
  key: "MixIsShowUpdateDialog",
  default: false,
});
/**
 * 取用户的策略信息
 */
export const MixStrategyUserInfo = atom({
  key: "MixStrategyUserInfo",
  default: {
    type: 1,
    isStrategyNormal: false, // 用户类型 1 用户（小白）  2 交易员(专家) 3 策略提供者（创建者）
    isStrategyTrader: false, // {boolean} 是否是策略交易员
    isStrategySupply: false, // {boolean} 是否是策略提供者
    isCopytradingTrader: false, // {boolean} 是否是交易专家（跟单）
  },
});

/**
 * 扩展网格气泡是否展示
 */
export const MixStrategyExpandGridTipShow = atom({
  key: "MixStrategyExpandGridTipShow",
  default: "", // 扩展网格气泡是否展示
});

/**
 * 策略可用体验券列表
 */
export const MixStrategyCouponsList = atom<Array<TicketByStrategyRes>>({
  key: "MixStrategyCouponsList",
  default: [],
});

/**
 * 快捷下单弹窗是否展示
 */
export const MixStrategyQuickDialogOpen = atom({
  key: "MixStrategyQuickDialogOpen",
  default: false,
});

// 记录下是否是手动切换activeKey（AI下单还是手动下单），非手动切换场景，AI推荐为空的时候需要自动切换到手动下单
export const MixStrategyIsSwitchActiveKeyByManual = atom({
  key: "MixStrategyIsSwitchActiveKeyByManual",
  default: false,
})